import { memo } from 'react';

import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

import SwitchInterviewSidebarButton from './SwitchInterviewSidebarButton';
import { Typography } from '@mui/material';
import { useInterviewOptions } from '@providers/InterviewsProvider';
import ArchivedInterviewsAccordion from './ArchivedInterviewsAccordion';
import { getNameFromId } from '@synergetx/puppet-interview-types';
import { useInterviewListOptions } from './InterviewList';
import { concatenate } from './types';
import useIsTallScreen from '@hooks/isTallScreen';

// split the concatenated key back into its parts for setting the selected interview
const splitConcatenatedKey = (key: string) => {
    const [groupId, interviewId] = key.split('_');
    return { groupId, interviewId };
};

const SwitchInterviewSidebarSelector = () => {
    const { allInterviews, availableInterviews, selectInterview, selectedInterview } =
        useInterviewOptions();
    const isTallScreen = useIsTallScreen();

    // Get interview options as direct children for MUI Select
    // (MUI Select requires MenuItems to be direct descendants)
    const { options: interviewOptions, CreateInterviewModal } = useInterviewListOptions();

    const selectedInterviewClaimKey = selectedInterview?.groupId
        ? concatenate(selectedInterview.groupId, selectedInterview.id)
        : '';

    // Check if selected interview is in available (non-archived) interviews
    // If not found (e.g., archived or not loaded), use empty string to avoid MUI warning
    const isSelectedInAvailable = availableInterviews?.some(
        (interview) =>
            interview.id === selectedInterview?.id &&
            interview.groupId === selectedInterview?.groupId,
    );
    const selectValue = isSelectedInAvailable ? selectedInterviewClaimKey : '';

    return allInterviews ? (
        <Box sx={{ minWidth: 120, marginX: 1, marginTop: isTallScreen ? 1 : 0.5 }}>
            <FormControl fullWidth size={isTallScreen ? 'medium' : 'small'}>
                <InputLabel id="interview-select-label">Survey</InputLabel>
                <Select
                    labelId="interview-select-label"
                    id="interview-select"
                    value={selectValue}
                    label="Survey"
                    onChange={(e) => {
                        const { groupId, interviewId } = splitConcatenatedKey(e.target.value);
                        if (groupId) selectInterview(interviewId, groupId);
                    }}
                    renderValue={InterviewName}
                >
                    {/* MenuItems must be direct children of Select for value matching to work */}
                    {interviewOptions}
                    <ArchivedInterviewsAccordion />
                </Select>
            </FormControl>
            {CreateInterviewModal}
        </Box>
    ) : (
        <SwitchInterviewSidebarButton />
    );
};

export default memo(SwitchInterviewSidebarSelector);

function InterviewName(key: string) {
    const { groupId, interviewId } = splitConcatenatedKey(key);
    const { selectedInterview } = useInterviewOptions();

    let name = getNameFromId(interviewId || '');

    if (selectedInterview?.id === interviewId && groupId === selectedInterview.groupId) {
        name = selectedInterview?.name || name;
    }
    return <Typography>{name}</Typography>;
}
